import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Check,
  CheckCircle2,
  Clock,
  Download,
  ExternalLink,
  Filter,
  Layers,
  Minus,
  Pencil,
  PhoneCall,
  Search,
  Shield,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Modal } from "../../components/ui/modal";
import { DatePicker } from "../../components/ui/date-picker";
import { Dropdown } from "../../components/ui/dropdown";
import { crmApi } from "./api";
import { EditUserModal } from "./edit-user-modal";
import { AddUserModal } from "./add-user-modal";
import { avatarColorOf, initialsOf, leadStatus, statusPillCls } from "./types";
import type { CrmMember, CrmRole } from "./types";

const ROLE_PILL_CLS: Record<string, string> = {
  admin: "bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20 font-bold",
  manager: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20 font-bold",
  agent: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 font-bold",
  "": "bg-stone-200/60 dark:bg-stone-800 text-ink-3 dark:text-stone-400 border border-line dark:border-white/10",
};
export const formatRoleName = (nameOrId?: string): string => {
  if (!nameOrId) return "Unassigned";
  const lower = nameOrId.toLowerCase().trim();
  if (lower === "admin" || lower === "business admin") return "Business Admin";
  if (lower === "manager" || lower === "sales manager") return "Sales Manager";
  if (lower === "agent" || lower === "sales" || lower === "sales agent" || lower === "sales rep") return "Sales Agent";
  if (lower === "unassigned") return "Unassigned";
  return nameOrId.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

export const getRoleKind = (role: string, roleMap: Map<string, CrmRole>): "admin" | "manager" | "agent" | "" => {
  if (role === "admin") return "admin";
  if (!role || role === "unassigned") return "";
  const custom = roleMap.get(role);
  const name = (custom?.name || role).toLowerCase();
  if (name.includes("admin")) return "admin";
  if (name.includes("manager")) return "manager";
  return "agent";
};

const roleLabel = (role: string, roleMap: Map<string, CrmRole>) => {
  if (role === "admin") return "Business Admin";
  if (!role || role === "unassigned") return "Unassigned";
  const custom = roleMap.get(role);
  if (custom) return formatRoleName(custom.name);
  return formatRoleName(role);
};
const roleAccessChips = (role: string, roleMap: Map<string, CrmRole>, defsLoaded: boolean) => {
  if (role === "admin") {
    return [{ label: "Full Workspace Access", cls: "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20 font-semibold" }];
  }
  if (!role) {
    return [{ label: "No CRM Access", cls: "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400 border-stone-200 dark:border-white/10" }];
  }
  const permissions = roleMap.get(role)?.permissions;
  if (!permissions) {
    if (!defsLoaded) {
      return role === "manager"
        ? [
            { label: "All Leads", cls: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20" },
            { label: "Assign", cls: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20" },
          ]
        : [{ label: "Own Leads Only", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" }];
    }
    return [{ label: "No Access", cls: "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400" }];
  }
  const chips: { label: string; cls: string }[] = [];
  if (permissions.view_all_leads) {
    chips.push({ label: "All Leads", cls: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20 font-medium" });
  } else {
    chips.push({ label: "Own Leads", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20 font-medium" });
  }
  if (permissions.edit_leads) chips.push({ label: "Edit", cls: "bg-stone-100 dark:bg-stone-800 text-ink-2 dark:text-stone-300 border border-line dark:border-white/10" });
  if (permissions.assign_leads) chips.push({ label: "Assign", cls: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20" });
  if (permissions.delete_leads) chips.push({ label: "Delete", cls: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20" });
  return chips;
};

const PRESENCE: Record<string, { label: string; dot: string; text: string }> = {
  "On call": { label: "On Call", dot: "bg-red-500", text: "text-red-600" },
  "No active call": { label: "Available", dot: "bg-green-500", text: "text-green-600" },
  "Disabled": { label: "Disabled", dot: "bg-ink-3", text: "text-ink-3" },
};
const presenceOf = (raw?: string) => PRESENCE[raw ?? ""] ?? { label: raw || "Unknown", dot: "bg-ink-3", text: "text-ink-3" };

const ROLE_CAPABILITY_CATEGORIES = [
  {
    title: "Lead Management",
    icon: Users,
    items: [
      { capability: "See own assigned leads", description: "View and work leads routed to the user", agent: true, manager: true, admin: true },
      { capability: "See all team leads", description: "Unrestricted pipeline visibility across all agents", agent: false, manager: true, admin: true },
      { capability: "Create / edit leads", description: "Add new contacts, update custom fields and status", agent: true, manager: true, admin: true },
      { capability: "Delete leads", description: "Permanently delete contact records from CRM", agent: false, manager: true, admin: true },
      { capability: "Assign & reassign leads", description: "Distribute or transfer leads between team members", agent: false, manager: true, admin: true },
    ],
  },
  {
    title: "Telephony & Dialing",
    icon: PhoneCall,
    items: [
      { capability: "Click-to-call & log outcomes", description: "Trigger calls and record customer responses", agent: true, manager: true, admin: true },
      { capability: "Power Dialer access", description: "Continuous automated queue dialing", agent: true, manager: true, admin: true },
      { capability: "Schedule follow-ups", description: "Set due dates and reminders on calendar", agent: true, manager: true, admin: true },
    ],
  },
  {
    title: "Reporting & Operations",
    icon: Sparkles,
    items: [
      { capability: "Team performance & reports", description: "Access conversion metrics, KPIs, and call logs", agent: false, manager: true, admin: true },
      { capability: "Attendance & Utilization", description: "View shift logs, talk minutes, and idle time", agent: false, manager: true, admin: true },
    ],
  },
  {
    title: "Administration & Access",
    icon: Shield,
    items: [
      { capability: "Manage team users & roles", description: "Configure CRM roles, permissions, and status", agent: false, manager: false, admin: true },
      { capability: "Invite users via Organisation", description: "Invite new team members and assign licenses", agent: false, manager: false, admin: true },
    ],
  },
];

export function CrmTeamPage({ agents = false }: { agents?: boolean }) {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userStatusFilter, setUserStatusFilter] = useState<"all" | "active" | "disabled">("all");
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  // Fetched whenever the viewer can manage the team, on either page — Users
  // & Roles rows are clickable too, and open the same stats drawer as
  // Agents, so it needs the same underlying data.
  // An unassigned viewer (role "") gets the same restricted view as an agent:
  // no roster/stats fetching (those endpoints would 403) and no team UI.
  const viewerRestricted = !team.data?.role || team.data.role === "agent";
  const canSeeAgentStats = !!team.data && !viewerRestricted;
  const stats = useQuery({ queryKey: ["crm-agents"], queryFn: crmApi.agents, enabled: canSeeAgentStats, refetchInterval: 15000 });
  const leadsQuery = useQuery({ queryKey: ["crm-agents-leads"], queryFn: crmApi.leads, enabled: canSeeAgentStats });
  const followUpsQuery = useQuery({ queryKey: ["crm-agents-followups"], queryFn: crmApi.followUps, enabled: canSeeAgentStats });
  // Only admins can list roles (same gate as edit_agent/create_crm_user) —
  // used to show real names/permissions for custom roles, and to populate
  // the Role dropdowns in Add/Edit user.
  const rolesQuery = useQuery({ queryKey: ["crm-roles"], queryFn: crmApi.roles, enabled: team.data?.role === "admin" });
  const roleMap = useMemo(() => new Map((rolesQuery.data ?? []).map(role => [role.id, role])), [rolesQuery.data]);
  // What a user can actually be assigned to — active, non-admin roles, plus
  // Unassigned (frees the user's current role so it can be deleted).
  // Falls back to the two starter roles if the roles list hasn't loaded yet, so
  // Edit user isn't stuck with an empty dropdown in the meantime.
  const assignableRoles = useMemo(() => {
    const list: { id: string; name: string }[] = [];
    if (rolesQuery.data && rolesQuery.data.length > 0) {
      for (const r of rolesQuery.data) {
        if (r.active !== false) {
          list.push({ id: r.id, name: formatRoleName(r.name) });
        }
      }
    } else {
      list.push(
        { id: "manager", name: "Sales Manager" },
        { id: "agent", name: "Sales Agent" },
      );
    }
    list.push({ id: "", name: "Unassigned" });
    return list;
  }, [rolesQuery.data]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);
  const [reassignError, setReassignError] = useState("");
  // Activity window for the Agents cards (Phase 2B #6): dialing volume in
  // the range, distinct from the all-time conversion stats beside it.
  const [activityRange, setActivityRange] = useState<"day" | "week" | "month" | "custom">("week");
  const [activityFrom, setActivityFrom] = useState("");
  const [activityTo, setActivityTo] = useState("");
  const todayUtc = () => new Date().toISOString().slice(0, 10);
  const [attendanceDay, setAttendanceDay] = useState(todayUtc);
  const attendanceQuery = useQuery({
    queryKey: ["crm-attendance", attendanceDay],
    queryFn: () => crmApi.attendance(attendanceDay),
    enabled: canSeeAgentStats && agents && !!attendanceDay,
    refetchInterval: 60000,
  });

  const exportAttendanceCsv = () => {
    const rows = attendanceQuery.data ?? [];
    const header = ["Name", "Email", "Role", "First seen", "Last seen", "Sessions", "Active minutes"];
    const cell = (v: string | number | null | undefined) => {
      const text = v ?? "";
      return /[",\n\r]/.test(String(text)) ? `"${String(text).replace(/"/g, '""')}"` : String(text);
    };
    const lines = [header.map(cell).join(",")];
    for (const row of rows) {
      lines.push([
        row.name, row.email, row.role,
        row.firstSeenAt ? new Date(row.firstSeenAt).toLocaleString() : "",
        row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString() : "",
        row.sessions, row.activeMinutes,
      ].map(cell).join(","));
    }
    const blob = new Blob([String.fromCharCode(0xFEFF) + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendance-${attendanceDay}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const activityReady = canSeeAgentStats && agents && (activityRange !== "custom" || (!!activityFrom && !!activityTo));
  const activityQuery = useQuery({
    queryKey: ["crm-activity", activityRange, activityFrom, activityTo],
    queryFn: () => crmApi.agentActivity(
      activityRange,
      activityRange === "custom" && activityFrom ? `${activityFrom}T00:00:00` : undefined,
      activityRange === "custom" && activityTo ? `${activityTo}T23:59:59` : undefined,
    ),
    enabled: activityReady,
    refetchInterval: 60000,
  });
  const activityById = useMemo(() => new Map((activityQuery.data?.agents ?? []).map(a => [a.id, a])), [activityQuery.data?.agents]);
  const activityLabel = activityRange === "day" ? "today" : activityRange === "week" ? "last 7 days" : activityRange === "month" ? "last 30 days" : "custom range";
  const rows = useMemo(() => (agents ? stats.data || [] : team.data?.members || []), [agents, stats.data, team.data?.members]);

  const filterRoleOptions = useMemo(() => {
    const opts = [
      { value: "all", label: "All Roles" },
      { value: "admin", label: "Business Admin" },
    ];
    if (rolesQuery.data && rolesQuery.data.length > 0) {
      for (const r of rolesQuery.data) {
        if (r.active !== false) {
          opts.push({ value: r.id, label: formatRoleName(r.name) });
        }
      }
    } else {
      opts.push(
        { value: "manager", label: "Sales Manager" },
        { value: "agent", label: "Sales Agent" },
      );
    }
    opts.push({ value: "unassigned", label: "Unassigned" });
    return opts;
  }, [rolesQuery.data]);

  const filterStatusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active Only" },
    { value: "disabled", label: "Disabled Only" },
  ];

  const filteredUserRows = useMemo(() => {
    if (agents) return rows;
    return rows.filter((r) => {
      if (userSearch.trim()) {
        const q = userSearch.toLowerCase();
        const match =
          r.name?.toLowerCase().includes(q) ||
          r.email?.toLowerCase().includes(q) ||
          r.phone?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (userRoleFilter !== "all") {
        if (userRoleFilter === "admin") {
          if (r.role !== "admin") return false;
        } else if (userRoleFilter === "unassigned") {
          if (r.role !== "") return false;
        } else {
          if (r.role !== userRoleFilter && getRoleKind(r.role, roleMap) !== userRoleFilter) {
            return false;
          }
        }
      }
      if (userStatusFilter === "active" && !r.active) return false;
      if (userStatusFilter === "disabled" && r.active) return false;
      return true;
    });
  }, [agents, rows, userSearch, userRoleFilter, userStatusFilter, roleMap]);
  // Users & Roles lists every account user via `rows` (team.members), but
  // its drawer shows live call/lead stats — pull those from `stats` (the
  // same crmApi.agents() data the Agents cards use) by id when available.
  const statsById = useMemo(() => new Map((stats.data || []).map(row => [row.id, row])), [stats.data]);
  const refresh = async () => { await Promise.all([cache.invalidateQueries({ queryKey: ["crm-team"] }), cache.invalidateQueries({ queryKey: ["crm-agents"] }), cache.invalidateQueries({ queryKey: ["crm-agents-leads"] })]); };
  const canAssign = team.data?.role === "admin" || !!team.data?.permissions?.assign_leads;
  const reassignAll = async () => {
    if (!selectedAgent || reassignBusy) return;
    setReassignBusy(true); setReassignError("");
    try {
      await crmApi.reassignAgentLeads(selectedAgent.id, reassignTarget || null);
      await refresh();
      setReassignOpen(false);
      setReassignTarget("");
    } catch (e) {
      setReassignError(e instanceof Error ? e.message : "Unable to reassign.");
    } finally {
      setReassignBusy(false);
    }
  };
  const toggleActive = async (row: CrmMember) => { setBusy(true); setError(""); try { await crmApi.editAgent(row.id, { role: row.role, active: !row.active }); await refresh(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); } };

  const selectedAgent = selectedAgentId ? statsById.get(selectedAgentId) ?? rows.find(row => row.id === selectedAgentId) ?? null : null;
  const agentLeads = selectedAgent ? (leadsQuery.data || []).filter(lead => lead.assignedAgentId === selectedAgent.id) : [];
  const pendingFollowUpsFor = (agentId: string) => (followUpsQuery.data || []).filter(f => f.assignedAgentId === agentId && !f.done).length;
  const [editingUser, setEditingUser] = useState<CrmMember | null>(null);
  const saveUserEdit = async (data: { name: string; email: string; phone: string; role: string; password?: string }) => {
    if (!editingUser) return;
    await crmApi.editAgent(editingUser.id, { ...data, active: editingUser.active });
    await refresh();
  };

  const totalAgents = rows.length;
  const availableAgents = rows.filter((r) => presenceOf(r.presence).label === "Available").length;
  const onCallAgents = rows.filter((r) => presenceOf(r.presence).label === "On Call").length;
  const totalCalls = rows.reduce(
    (sum, r) => sum + (activityById.get(r.id)?.totalCalls ?? r.calls ?? 0),
    0,
  );
  const totalConnected = rows.reduce(
    (sum, r) => sum + (activityById.get(r.id)?.connectedCalls ?? 0),
    0,
  );
  const totalLeads = rows.reduce((sum, r) => sum + (r.leads ?? 0), 0);
  const totalInterested = rows.reduce((sum, r) => sum + (r.interested ?? 0), 0);
  const teamConversionRate = totalCalls > 0 ? Math.round((totalInterested / totalCalls) * 100) : 0;
  const connectRate = totalCalls > 0 ? Math.round((totalConnected / totalCalls) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* 1. Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-base font-semibold text-ink dark:text-white">
            {agents ? "Agents" : "Users & Roles"}
          </h1>
          {agents ? (
            <>
              <span className="rounded-full bg-stone-100 dark:bg-stone-800 px-2.5 py-0.5 text-xs font-semibold text-ink-3 dark:text-stone-400">
                {rows.length} total
              </span>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                </span>
                <span>{availableAgents} Available Now</span>
              </div>
              {onCallAgents > 0 && (
                <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                  ● {onCallAgents} On Call
                </span>
              )}
            </>
          ) : (
            <span className="rounded-full bg-stone-100 dark:bg-stone-800 px-2.5 py-0.5 text-xs font-semibold text-ink-3 dark:text-stone-400">
              {rows.length} users
            </span>
          )}
        </div>

        {agents ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex items-center rounded-lg border border-line dark:border-white/10 bg-elevated dark:bg-[#262320] p-1 shadow-2xs">
              {(["day", "week", "month", "custom"] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setActivityRange(r)}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-xs font-semibold transition-all",
                    activityRange === r
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white hover:bg-stone-100 dark:hover:bg-white/5",
                  )}
                >
                  {r === "day" ? "Today" : r === "week" ? "7 days" : r === "month" ? "30 days" : "Custom"}
                </button>
              ))}
            </div>
            {activityRange === "custom" && (
              <div className="flex items-center gap-1.5">
                <DatePicker
                  value={activityFrom}
                  onChange={setActivityFrom}
                  placeholder="From date"
                  maxDate={activityTo || todayUtc()}
                />
                <span className="text-xs text-ink-3 dark:text-stone-400">to</span>
                <DatePicker
                  value={activityTo}
                  onChange={setActivityTo}
                  placeholder="To date"
                  minDate={activityFrom}
                  maxDate={todayUtc()}
                  align="right"
                />
              </div>
            )}
          </div>
        ) : (
          team.data?.role === "admin" && (
            <div className="flex gap-2">
              <button
                onClick={() => navigate("/crm/roles")}
                className="flex items-center gap-1.5 rounded-lg border border-line dark:border-white/10 bg-elevated dark:bg-[#262320] px-3.5 py-1.5 text-xs font-semibold text-ink dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-white/5 active:scale-[0.98] transition-all"
              >
                <Shield className="h-3.5 w-3.5" /> Manage roles
              </button>
              <button
                onClick={() => setAddUserOpen(true)}
                className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all"
              >
                + Add User
              </button>
            </div>
          )
        )}
      </div>

      {!agents && (
        <p className="text-xs text-ink-3 dark:text-stone-400">
          The organisation owner is the Business Admin. Sales Agents can view and interact only with leads assigned to them.
        </p>
      )}
      {(error || team.error || stats.error) && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4 text-xs font-semibold text-red-600 dark:text-red-400"
        >
          {error || ((team.error || stats.error) as Error).message}
        </div>
      )}
      {team.isLoading && <p className="text-xs text-ink-3 dark:text-stone-400">Loading team…</p>}

      {team.data && viewerRestricted ? (
        <div className="rounded-xl border border-line dark:border-white/10 bg-elevated dark:bg-[#201e1b] p-6 text-center text-sm text-ink-3 dark:text-stone-400">
          Team management is available to business admins and sales managers.
        </div>
      ) : agents ? (
        <>
          {/* 2. Team-Wide Top Summary KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Total Roster */}
            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Sales Roster</span>
                <Users className="h-3.5 w-3.5 text-brand" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{totalAgents}</span>
                <span className="text-[11px] text-ink-3 dark:text-stone-400">reps</span>
              </div>
              <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">
                {availableAgents} available · {onCallAgents} on call
              </div>
            </div>

            {/* Team Dialing Volume */}
            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Calls ({activityLabel})</span>
                <PhoneCall className="h-3.5 w-3.5 text-brand" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{totalCalls}</span>
                <span className="text-[11px] text-ink-3 dark:text-stone-400">dials</span>
              </div>
              <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">
                {totalConnected} connected ({connectRate}%)
              </div>
            </div>

            {/* Assigned Pipeline */}
            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Assigned Leads</span>
                <Layers className="h-3.5 w-3.5 text-brand" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{totalLeads}</span>
                <span className="text-[11px] text-ink-3 dark:text-stone-400">in pipeline</span>
              </div>
              <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">
                Active across {totalAgents} reps
              </div>
            </div>

            {/* Team Conversion / Interest Rate */}
            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Interest Rate</span>
                <Sparkles className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{teamConversionRate}%</span>
                <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">avg converted</span>
              </div>
              <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">
                {totalInterested} leads marked interested
              </div>
            </div>
          </div>

          {/* 3. Modern Agent Performance Cards */}
          <div className="grid gap-3.5 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((row) => {
              const presence = presenceOf(row.presence);
              const pct = row.calls ? Math.round(((row.interested ?? 0) / row.calls) * 100) : 0;
              const activity = activityById.get(row.id);
              const calls = activity?.totalCalls ?? row.calls ?? 0;
              const connected = activity?.connectedCalls ?? 0;
              const pendingFollowups = pendingFollowUpsFor(row.id);
              const roleName = roleLabel(row.role, roleMap);

              return (
                <div
                  key={row.id}
                  onClick={() => setSelectedAgentId(row.id)}
                  className="group cursor-pointer flex flex-col justify-between rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] p-4 sm:p-5 shadow-card dark:shadow-card-dark transition-all hover:border-brand/40 hover:shadow-md"
                >
                  <div>
                    {/* Top Header: Avatar + Info + Role */}
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-line dark:border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-stone-200 dark:bg-stone-700 text-xs font-bold text-ink dark:text-white">
                            {initialsOf(row.name)}
                          </div>
                          <span
                            aria-hidden
                            className={cn(
                              "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white dark:ring-[#201e1b]",
                              presence.dot,
                            )}
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="truncate text-xs font-semibold text-ink dark:text-white group-hover:text-brand transition-colors">
                              {row.name}
                            </h2>
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-ink-3 dark:text-stone-400">
                            <span>{roleName}</span>
                            <span>·</span>
                            <span className={cn("font-medium", presence.text)}>{presence.label}</span>
                          </div>
                        </div>
                      </div>

                      <span
                        className={cn(
                          "shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold border",
                          row.role === "admin"
                            ? "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20"
                            : row.role === "manager"
                            ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
                            : "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
                        )}
                      >
                        {roleName}
                      </span>
                    </div>

                    {/* 3-Cell Metrics Grid (Tabular divider style) */}
                    <div className="mt-3.5 grid grid-cols-3 divide-x divide-line dark:divide-white/10 rounded-lg border border-line dark:border-white/10 bg-stone-50/60 dark:bg-white/[0.02] py-2 text-center">
                      <div className="px-2">
                        <span className="block text-base font-bold text-ink dark:text-white">{calls}</span>
                        <span className="text-[10px] text-ink-3 dark:text-stone-400">
                          {activity ? `${connected} conn.` : "Calls"}
                        </span>
                      </div>
                      <div className="px-2">
                        <span className="block text-base font-bold text-ink dark:text-white">{row.leads ?? 0}</span>
                        <span className="text-[10px] text-ink-3 dark:text-stone-400">Leads</span>
                      </div>
                      <div className="px-2">
                        <span className="block text-base font-bold text-ink dark:text-white">{pct}%</span>
                        <span className="text-[10px] text-ink-3 dark:text-stone-400">Interested</span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="mt-3 flex items-center justify-between pt-2 border-t border-line dark:border-white/10 text-[11px] text-ink-3 dark:text-stone-400">
                    <div className="flex items-center gap-1.5">
                      {pendingFollowups > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400 border border-amber-500/20">
                          {pendingFollowups} follow-up{pendingFollowups === 1 ? "" : "s"} due
                        </span>
                      ) : (
                        <span>No pending follow-ups</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 font-semibold text-brand text-[11px] group-hover:translate-x-0.5 transition-transform">
                      <span>Details</span>
                      <ArrowUpRight className="h-3 w-3" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 4. Modern Attendance & Utilization Section */}
          <section className="mt-4 rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] p-4 sm:p-5 shadow-card dark:shadow-card-dark">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3.5 border-b border-line dark:border-white/10">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-brand" />
                <h2 className="text-base font-semibold text-ink dark:text-white">Attendance & Utilization</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setAttendanceDay(todayUtc())}
                  className={cn(
                    "rounded-lg px-2.5 py-1 text-xs font-semibold border transition-all",
                    attendanceDay === todayUtc()
                      ? "border-brand bg-brand/10 text-brand"
                      : "border-line dark:border-white/10 text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
                  )}
                >
                  Today
                </button>
                <DatePicker
                  value={attendanceDay}
                  onChange={setAttendanceDay}
                  maxDate={todayUtc()}
                  align="right"
                />
                <button
                  disabled={!attendanceQuery.data?.length}
                  onClick={exportAttendanceCsv}
                  className="flex items-center gap-1.5 rounded-lg border border-line dark:border-white/10 bg-white dark:bg-stone-800 px-3 py-1 text-xs font-semibold text-ink dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-white/5 disabled:opacity-50 transition-all active:scale-[0.98]"
                >
                  <Download className="h-3.5 w-3.5 text-brand" />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            {attendanceQuery.isLoading && <p className="mt-4 text-xs text-ink-3 dark:text-stone-400">Loading attendance data…</p>}
            {attendanceQuery.error && (
              <p role="alert" className="mt-4 text-xs font-semibold text-red-600 dark:text-red-400">
                {(attendanceQuery.error as Error).message}
              </p>
            )}

            {attendanceQuery.data && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-line dark:border-white/10 bg-stone-50 dark:bg-stone-900/60 font-semibold text-ink-3 dark:text-stone-400">
                    <tr>
                      <th className="py-2.5 px-3">Agent</th>
                      <th className="py-2.5 px-3">First Seen</th>
                      <th className="py-2.5 px-3">Last Seen</th>
                      <th className="py-2.5 px-3">Sessions</th>
                      <th className="py-2.5 px-3">Activity</th>
                      <th className="py-2.5 px-3">Calling Utilization</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line dark:divide-white/10">
                    {attendanceQuery.data.map((row) => (
                      <tr key={row.id} className="transition-colors hover:bg-stone-50/75 dark:hover:bg-white/5">
                        <td className="py-2.5 px-3 align-middle">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-200 dark:bg-stone-700 text-xs font-bold text-ink dark:text-white">
                              {initialsOf(row.name)}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-ink dark:text-white">{row.name}</p>
                              <p className="truncate text-[11px] text-ink-3 dark:text-stone-400">{row.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap py-2.5 px-3 align-middle font-mono text-ink-3 dark:text-stone-400">
                          {row.firstSeenAt ? new Date(row.firstSeenAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="whitespace-nowrap py-2.5 px-3 align-middle font-mono text-ink-3 dark:text-stone-400">
                          {row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="py-2.5 px-3 align-middle font-semibold text-ink dark:text-white">{row.sessions}</td>
                        <td className="whitespace-nowrap py-2.5 px-3 align-middle">
                          {row.sessions === 0 ? (
                            <span className="rounded-md bg-stone-100 dark:bg-stone-800 px-2 py-0.5 text-[11px] font-semibold text-ink-3 dark:text-stone-400">
                              Absent
                            </span>
                          ) : (
                            <span className="rounded-md bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                              ~{row.activeMinutes} min active
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2.5 px-3 align-middle">
                          {row.utilization === null ? (
                            <span className="text-ink-3 dark:text-stone-400">—</span>
                          ) : (
                            <div className="min-w-[140px]">
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-700">
                                  <div className="h-full rounded-full bg-brand" style={{ width: `${row.utilization}%` }} />
                                </div>
                                <span className="font-mono text-xs font-semibold text-ink dark:text-white">{row.utilization}%</span>
                              </div>
                              <p className="mt-0.5 text-[10px] text-ink-3 dark:text-stone-400 font-mono">
                                {row.callMinutes}m calls · {row.idleMinutes}m idle
                              </p>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          {/* Top Health & Access KPI Cards for Users & Roles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-line dark:border-white/10 bg-elevated p-3.5 shadow-2xs">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Total Members</span>
                <Users className="h-3.5 w-3.5 text-brand" />
              </div>
              <span className="text-xl font-bold text-ink dark:text-white">{rows.length}</span>
              <p className="text-[11px] text-ink-3 dark:text-stone-400 mt-0.5">Configured in account</p>
            </div>

            <div className="rounded-xl border border-line dark:border-white/10 bg-elevated p-3.5 shadow-2xs">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Available Now</span>
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {rows.filter((r) => r.active && presenceOf(r.presence).label === "Available").length}
              </span>
              <p className="text-[11px] text-ink-3 dark:text-stone-400 mt-0.5">Online & ready to dial</p>
            </div>

            <div className="rounded-xl border border-line dark:border-white/10 bg-elevated p-3.5 shadow-2xs">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Sales Agents</span>
                <PhoneCall className="h-3.5 w-3.5 text-blue-500" />
              </div>
              <span className="text-xl font-bold text-ink dark:text-white">
                {rows.filter((r) => getRoleKind(r.role, roleMap) === "agent").length}
              </span>
              <p className="text-[11px] text-ink-3 dark:text-stone-400 mt-0.5">Frontline reps</p>
            </div>

            <div className="rounded-xl border border-line dark:border-white/10 bg-elevated p-3.5 shadow-2xs">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Admins & Managers</span>
                <Shield className="h-3.5 w-3.5 text-purple-500" />
              </div>
              <span className="text-xl font-bold text-ink dark:text-white">
                {rows.filter((r) => { const k = getRoleKind(r.role, roleMap); return k === "admin" || k === "manager"; }).length}
              </span>
              <p className="text-[11px] text-ink-3 dark:text-stone-400 mt-0.5">Leadership & oversight</p>
            </div>
          </div>

          {/* Search, Role Filters, Status Controls */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line dark:border-white/10 bg-elevated p-3">
            <div className="flex flex-1 items-center gap-2 min-w-[240px]">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
                <input
                  type="text"
                  placeholder="Search users by name, email, phone…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full rounded-lg border border-line dark:border-white/10 bg-soft dark:bg-stone-900/60 py-1.5 pl-9 pr-3 text-xs text-ink dark:text-white outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
              </div>

              {/* Role filter */}
              <div className="flex items-center gap-1.5 w-44">
                <Filter className="h-3.5 w-3.5 text-ink-3 shrink-0" />
                <Dropdown
                  size="sm"
                  ariaLabel="Filter by role"
                  value={userRoleFilter}
                  options={filterRoleOptions}
                  onChange={setUserRoleFilter}
                  className="bg-soft dark:bg-stone-900/60"
                />
              </div>

              {/* Status filter */}
              <div className="w-36">
                <Dropdown
                  size="sm"
                  ariaLabel="Filter by status"
                  value={userStatusFilter}
                  options={filterStatusOptions}
                  onChange={(v) => setUserStatusFilter(v as any)}
                  className="bg-soft dark:bg-stone-900/60"
                />
              </div>
            </div>

            <span className="text-xs text-ink-3 dark:text-stone-400 font-mono">
              Showing {filteredUserRows.length} of {rows.length} {rows.length === 1 ? "user" : "users"}
            </span>
          </div>

          {/* User Table */}
          <div className="mt-3 overflow-x-auto rounded-xl border border-line dark:border-white/10 bg-elevated shadow-2xs">
            <table className="w-full min-w-[760px] text-left text-xs">
              <thead className="border-b border-line dark:border-white/10 bg-stone-50 dark:bg-stone-900/60 font-semibold text-ink-3 dark:text-stone-400">
                <tr>
                  <th className="py-3 px-4 w-[24%]">User</th>
                  <th className="py-3 px-3 w-[20%]">Email</th>
                  <th className="py-3 px-3 w-[14%]">Phone</th>
                  <th className="py-3 px-3 w-[12%]">Role</th>
                  <th className="py-3 px-3 w-[16%]">Capabilities</th>
                  <th className="py-3 px-3 w-[8%]">Status</th>
                  <th className="py-3 px-3 w-[12%] text-right pr-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line dark:divide-white/10">
                {filteredUserRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-ink-3 dark:text-stone-400">
                      No users match the search filters.
                    </td>
                  </tr>
                ) : (
                  filteredUserRows.map((row) => {
                    const presence = presenceOf(row.presence);
                    const accessChips = roleAccessChips(row.role, roleMap, rolesQuery.data !== undefined);
                    const roleKind = getRoleKind(row.role, roleMap);

                    return (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedAgentId(row.id)}
                        className="cursor-pointer transition-colors hover:bg-stone-50/75 dark:hover:bg-white/5"
                      >
                        <td className="py-3 px-4 align-middle">
                          <div className="flex items-center gap-3">
                            <div className="relative shrink-0">
                              <div
                                className={cn(
                                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white shadow-2xs",
                                  avatarColorOf(row.id),
                                )}
                              >
                                {initialsOf(row.name)}
                              </div>
                              <span
                                title={presence.label}
                                className={cn(
                                  "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-[#201e1b]",
                                  presence.dot,
                                )}
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-bold text-ink dark:text-white">{row.name}</p>
                              <p className="truncate text-[10px] text-ink-3 dark:text-stone-400 font-mono">
                                ID: {row.id.slice(0, 8)}…
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3 align-middle font-mono text-ink-2 dark:text-stone-300">
                          {row.email || "—"}
                        </td>
                        <td className="py-3 px-3 align-middle font-mono text-ink-2 dark:text-stone-300">
                          {row.phone || "—"}
                        </td>
                        <td className="py-3 px-3 align-middle">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide",
                              ROLE_PILL_CLS[roleKind] ?? ROLE_PILL_CLS.agent,
                            )}
                          >
                            {roleLabel(row.role, roleMap)}
                          </span>
                        </td>
                        <td className="py-3 px-3 align-middle">
                          <div className="flex flex-wrap gap-1">
                            {accessChips.map((chip, idx) => (
                              <span
                                key={idx}
                                className={cn(
                                  "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold border",
                                  chip.cls,
                                )}
                              >
                                {chip.label}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-3 align-middle">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                              row.active
                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20"
                                : "bg-stone-200/60 dark:bg-stone-800 text-ink-3 dark:text-stone-400",
                            )}
                          >
                            {row.active ? "Active" : "Disabled"}
                          </span>
                        </td>
                        <td
                          className="py-3 px-3 pr-4 align-middle text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1.5">
                            <Link
                              to={`/crm/leads?agent=${encodeURIComponent(row.id)}`}
                              title="View assigned leads in CRM"
                              className="rounded-lg p-1.5 text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white transition-colors"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                            {row.role !== "admin" && team.data?.role === "admin" && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setEditingUser(row)}
                                  title="Edit user details"
                                  className="rounded-lg p-1.5 text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white transition-colors"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => void toggleActive(row)}
                                  className={cn(
                                    "rounded-lg px-2.5 py-1 text-xs font-semibold transition-all",
                                    row.active
                                      ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
                                      : "text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20",
                                  )}
                                >
                                  {row.active ? "Disable" : "Enable"}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!agents && team.data && !viewerRestricted && (
        <section className="mt-8 rounded-xl border border-line dark:border-white/10 bg-elevated p-5 shadow-2xs">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold text-ink dark:text-white">Role Capabilities & Permissions Matrix</h2>
              <p className="text-xs text-ink-3 dark:text-stone-400 mt-0.5">
                Exact capability comparison across standard CRM roles.
              </p>
            </div>
            {team.data?.role === "admin" && (
              <button
                onClick={() => navigate("/crm/roles")}
                className="flex items-center gap-1.5 text-xs font-semibold text-brand hover:underline"
              >
                <Shield className="h-3.5 w-3.5" /> Manage Custom Roles
              </button>
            )}
          </div>

          <div className="space-y-4">
            {ROLE_CAPABILITY_CATEGORIES.map((category) => {
              const CategoryIcon = category.icon;
              return (
                <div key={category.title} className="rounded-xl border border-line dark:border-white/10 overflow-hidden">
                  <div className="flex items-center gap-2 bg-stone-50/80 dark:bg-stone-900/60 px-4 py-2 border-b border-line dark:border-white/10">
                    <CategoryIcon className="h-4 w-4 text-brand" />
                    <span className="text-xs font-bold text-ink dark:text-white uppercase tracking-wider font-mono">
                      {category.title}
                    </span>
                  </div>

                  <table className="w-full table-fixed text-left text-xs">
                    <colgroup>
                      <col className="w-[52%]" />
                      <col className="w-[16%]" />
                      <col className="w-[16%]" />
                      <col className="w-[16%]" />
                    </colgroup>
                    <thead>
                      <tr className="border-b border-line dark:border-white/10 bg-white dark:bg-stone-900/30 text-ink-3 dark:text-stone-400 font-semibold">
                        <th className="py-2.5 px-4">Capability & Scope</th>
                        <th className="py-2.5 px-3 text-center">Sales Agent</th>
                        <th className="py-2.5 px-3 text-center">Sales Manager</th>
                        <th className="py-2.5 px-3 text-center">Business Admin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line dark:divide-white/10">
                      {category.items.map((item) => (
                        <tr key={item.capability} className="hover:bg-stone-50/50 dark:hover:bg-white/5 transition-colors">
                          <td className="py-2.5 px-4">
                            <p className="font-semibold text-ink dark:text-white">{item.capability}</p>
                            <p className="text-[11px] text-ink-3 dark:text-stone-400">{item.description}</p>
                          </td>
                          <td className="py-2.5 px-3 text-center align-middle">
                            {item.agent ? (
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold mx-auto">
                                <Check className="h-3 w-3 stroke-[3]" />
                              </span>
                            ) : (
                              <span className="text-ink-3 dark:text-stone-600 font-bold">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-center align-middle">
                            {item.manager ? (
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold mx-auto">
                                <Check className="h-3 w-3 stroke-[3]" />
                              </span>
                            ) : (
                              <span className="text-ink-3 dark:text-stone-600 font-bold">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-center align-middle">
                            {item.admin ? (
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 font-bold mx-auto">
                                <Check className="h-3 w-3 stroke-[3]" />
                              </span>
                            ) : (
                              <span className="text-ink-3 dark:text-stone-600 font-bold">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {editingUser && (
        <EditUserModal user={editingUser} roles={assignableRoles} onClose={() => setEditingUser(null)} onSave={saveUserEdit} />
      )}

      {addUserOpen && (
        <AddUserModal
          roles={assignableRoles}
          currentCrmMembers={rows}
          onClose={() => setAddUserOpen(false)}
          onRefresh={refresh}
        />
      )}

      {/* Agent detail drawer — a slide-over overlay opened by clicking a card. */}
      {selectedAgent && (() => {
        const presence = presenceOf(selectedAgent.presence);
        const pct = selectedAgent.calls ? Math.round(((selectedAgent.interested ?? 0) / selectedAgent.calls) * 100) : 0;
        const rowsInfo: [string, string | number][] = [
          ["Calls today", selectedAgent.calls ?? 0],
          ["Leads owned", selectedAgent.leads ?? 0],
          ["Interested this shift", selectedAgent.interested ?? 0],
          ["Interested rate", `${pct}%`],
          ["Pending follow-ups", pendingFollowUpsFor(selectedAgent.id)],
          ["Email", selectedAgent.email],
          ["Phone", selectedAgent.phone],
        ];
        return (
          <div className="fixed inset-0 z-40 flex justify-end bg-ink/30" onClick={() => setSelectedAgentId(null)}>
            <div onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-md overflow-y-auto bg-elevated p-6 shadow-2xl sm:max-w-lg">
              <div className="flex items-start justify-between">
                <div className={cn("flex h-16 w-16 items-center justify-center rounded-full text-xl font-bold text-white", avatarColorOf(selectedAgent.id))}>
                  {initialsOf(selectedAgent.name)}
                </div>
                <button onClick={() => setSelectedAgentId(null)} aria-label="Close" className="rounded-full bg-soft p-2 text-ink-2 hover:bg-line">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <h2 className="mt-4 text-xl font-bold text-ink">{selectedAgent.name}</h2>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-3">
                {roleLabel(selectedAgent.role, roleMap)}
                <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", presence.dot)} />
                <span className={presence.text}>{presence.label}</span>
              </p>

              <div className="mt-5 border-t border-line">
                {rowsInfo.map(([label, value]) => (
                  <div key={label} className="flex justify-between border-b border-line py-2.5 text-sm">
                    <span className="text-ink-3">{label}</span>
                    <span className="font-semibold text-ink">{value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">Leads assigned ({agentLeads.length})</p>
                {canAssign && selectedAgent.role !== "admin" && agentLeads.length > 0 && (
                  <button onClick={() => { setReassignError(""); setReassignTarget(""); setReassignOpen(true); }} className="text-xs font-semibold text-brand hover:underline">
                    Reassign all…
                  </button>
                )}
              </div>
              <div className="mt-2 border-t border-line">
                {agentLeads.length === 0 ? (
                  <p className="py-4 text-sm text-ink-3">No leads assigned.</p>
                ) : (
                  agentLeads.map(lead => (
                    <div key={lead.id} className="flex items-center justify-between border-b border-line py-2.5 text-sm">
                      <span className="truncate text-ink">{lead.name || lead.contactId || "Unnamed lead"}</span>
                      <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", statusPillCls(leadStatus(lead)))}>{leadStatus(lead)}</span>
                    </div>
                  ))
                )}
              </div>

              {reassignOpen && (
                <Modal
                  title={`Reassign ${selectedAgent.name}'s leads`}
                  subtitle="Only open leads move — Converted and Not interested stay. Each move is logged in that lead's history."
                  onClose={() => !reassignBusy && setReassignOpen(false)}
                  width="max-w-md"
                  footer={
                    <>
                      <button disabled={reassignBusy} onClick={() => setReassignOpen(false)} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">Cancel</button>
                      <button disabled={reassignBusy} onClick={() => void reassignAll()} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
                        {reassignBusy ? "Moving…" : `Move ${agentLeads.length} lead(s)`}
                      </button>
                    </>
                  }
                >
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-ink-2">Move to</label>
                    <select value={reassignTarget} onChange={e => { setReassignTarget(e.target.value); setReassignError(""); }} className="w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm text-ink outline-none focus:border-brand">
                      <option value="">Unassigned queue</option>
                      {(team.data?.members ?? []).filter(m => m.active && m.id !== selectedAgent.id).map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  {reassignError && <p role="alert" className="mt-3 text-xs font-semibold text-red-500">{reassignError}</p>}
                </Modal>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
